import React, { useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ImageryProvider } from '@appcues/imagery-kit';
import { ThemeEditorProvider } from '@appcues/theme-editor';
import { createAnalyticsClient } from '@appcues/analytics/client';
import { AnalyticsContext } from '@appcues/analytics/context';
import { getRegionFromHostname } from 'ext/lib/auth/web';
import { AuthShape } from 'lib/auth-shape';
import UserShape from 'entities/user/shape';
import StyleProvider from 'styles/StyleProvider';
import configureStore from './configure-store';
import rootReducer from './root-reducer';
import rootSaga from './root-saga';

export default function Providers({
  auth,
  user,
  endpointOverrides,
  studioUrl,
  region,
  children,
}) {
  const [store, setStore] = useState(null);

  useLayoutEffect(() => {
    (async () => {
      const result = await configureStore({
        auth,
        user,
        endpointOverrides,
        studioUrl,
        region,
        rootReducer,
        rootSaga,
      });
      setStore(result);
    })();
  }, [auth, user, endpointOverrides, studioUrl, region]);

  // return null while store has been created, since now it configureStore is async
  if (!store) {
    return null;
  }

  const { store: reduxStore, apiClient } = store;

  const isEuRegion = getRegionFromHostname() === 'EU';

  const analyticsClient = createAnalyticsClient(
    reduxStore.getState(),
    {
      app: 'Mobile Builder',
    },
    true, // client mode
    isEuRegion
      ? undefined
      : {
          logrocket: {
            commitHash: import.meta.env.COMMIT_HASH,
            environment: import.meta.env.APPCUES_ENV,
            isProd: import.meta.env.APPCUES_ENV === 'prod',
          },
        }
  );

  return (
    <Provider store={reduxStore}>
      <AnalyticsContext.Provider value={analyticsClient}>
        <DndProvider backend={HTML5Backend}>
          <StyleProvider>
            <ImageryProvider
              fetchImages={apiClient.fetchImages}
              uploadImage={apiClient.uploadImage}
            >
              <ThemeEditorProvider
                fetchTheme={apiClient.getExperienceTheme}
                saveTheme={apiClient.updateExperienceTheme}
                publishTheme={apiClient.publishExperienceTheme}
              >
                {children}
              </ThemeEditorProvider>
            </ImageryProvider>
          </StyleProvider>
        </DndProvider>
      </AnalyticsContext.Provider>
    </Provider>
  );
}

Providers.propTypes = {
  auth: AuthShape,
  user: UserShape,
  endpointOverrides: PropTypes.shape({
    customerApiUrl: PropTypes.string,
    appcuesApiUrl: PropTypes.string,
    imageServiceLambdaUrl: PropTypes.string,
  }),
  studioUrl: PropTypes.string,
  region: PropTypes.string,
  children: PropTypes.node.isRequired,
};
